import {
  ACKNOWLEDGEMENT_LOOKBACK_MS,
  type FeedWatermark,
  foldAcknowledgements,
  isAcknowledged,
  NO_ACKNOWLEDGEMENTS,
  type TargetAcknowledgement,
  watermarkOf,
} from "@storyboard/dispatch-view/acknowledgement";
import {
  type AcknowledgeableSource,
  type AcknowledgeableTmsCategory,
  DEFAULT_REQUIRES_ACKNOWLEDGEMENT,
  resolveRequiresAcknowledgement,
} from "@storyboard/dispatch-view/requiresAcknowledgement";
import type { OpenTask } from "@storyboard/dispatch-view/task";
import { isHandledByTask, taskSubjectKey } from "@storyboard/dispatch-view/task";
import { tmsEventCategoriesStatedBy } from "@storyboard/tms-event-contracts/tmsEventSubjects";
import {
  ACKNOWLEDGEMENT_SUBJECTS,
  isAnchorAcknowledgementSubject,
} from "@/models/acknowledgement/acknowledgementStore";
import { idx, rowsByIndex, store } from "@/store/store";
import type { Item, TmsEventRow } from "@/store/types";
import type { AffectedKeys } from "@/store/useKeyedCompute";
import {
  eventTargetOf,
  type ItemTarget,
  NO_TASKS,
  NO_TASKS_ELSEWHERE,
  type TaskFold,
  type TasksElsewhere,
} from "./tasks";

/**
 * What a view asks its team to acknowledge, resolved: the predicate over messages and TMS events.
 */
export type AcknowledgementRequirement = (item: AcknowledgeableSource) => boolean;

/** What a view that names no Requires Acknowledgement list asks for: `DEFAULT_REQUIRES_ACKNOWLEDGEMENT`, resolved. */
export const DEFAULT_REQUIREMENT: AcknowledgementRequirement = resolveRequiresAcknowledgement(
  DEFAULT_REQUIRES_ACKNOWLEDGEMENT,
);

/** The collections the acknowledgement selectors read, for `useCollectionsCompute`. */
export const acknowledgementCollections = [
  "feedAcknowledgement",
  "feedItemAcknowledgement",
  "anchorAcknowledgement",
  "item",
  "tmsEvent",
  "feed",
  "task",
  "taskPinBoard",
  "taskAcknowledgement",
] as const;

/** A channel's watermark rows, folded. */
export function feedFold(feedId: string): TargetAcknowledgement {
  return foldAcknowledgements(rowsByIndex("feedAcknowledgement", idx.feedAcknowledgementsByFeed, feedId));
}

/** The watermark standing on a channel, or null. */
export function feedWatermark(feedId: string): FeedWatermark | null {
  return watermarkOf(feedFold(feedId));
}

/** One message's own acknowledgements, folded. */
export function feedItemFold(feedItemId: string): TargetAcknowledgement {
  return foldAcknowledgements(rowsByIndex("feedItemAcknowledgement", idx.feedItemAcknowledgementsByItem, feedItemId));
}

/**
 * The TMS event subjects that have an acknowledgement log. An event of any other subject is never an item: nothing
 * could record that it was handled.
 */
const ACKNOWLEDGEABLE_TMS_SUBJECTS: ReadonlySet<string> = new Set(
  ACKNOWLEDGEMENT_SUBJECTS.filter(isAnchorAcknowledgementSubject),
);

/**
 * The TMS categories a Requires Acknowledgement list may name: only kinds whose subject has an acknowledgement log.
 */
export const ACKNOWLEDGEABLE_TMS_CATEGORIES: readonly AcknowledgeableTmsCategory[] =
  tmsEventCategoriesStatedBy(ACKNOWLEDGEABLE_TMS_SUBJECTS);

/** Whether a TMS event's subject has an acknowledgement log. */
export function isAcknowledgeableTmsEvent(event: Pick<TmsEventRow, "subject">): boolean {
  return ACKNOWLEDGEABLE_TMS_SUBJECTS.has(event.subject);
}

/** One TMS event's own acknowledgements, folded; keyed on its anchor, which is the event's id. */
export function tmsEventFold(event: Pick<TmsEventRow, "id" | "subject">): TargetAcknowledgement {
  if (!isAcknowledgeableTmsEvent(event)) return NO_ACKNOWLEDGEMENTS;
  return foldAcknowledgements(rowsByIndex("anchorAcknowledgement", idx.anchorAcknowledgementsByAnchor, event.id));
}

/** One task's own acknowledgements, folded: done while one stands. */
export function taskFold(taskId: string): TargetAcknowledgement {
  return foldAcknowledgements(rowsByIndex("taskAcknowledgement", idx.taskAcknowledgementsByTask, taskId));
}

/** When a TMS event reached the server: its row's `createdAt`, falling back on `occurredAt` for a row served without. */
export function tmsEventArrivedAt(event: Pick<TmsEventRow, "createdAt" | "occurredAt">): string {
  return event.createdAt ?? event.occurredAt;
}

/** Whether a store item is one of the driver's own live messages: the only messages that can require acknowledgement. */
export function isDriverMessage(item: Item): boolean {
  return item.isFromAliasMember === true && item.deletedAt == null;
}

/** A TMS event as the Requires Acknowledgement predicate reads it. */
export function tmsEventSource(event: Pick<TmsEventRow, "category" | "kind">): AcknowledgeableSource {
  return { source: "tmsEvent", category: event.category ?? null, kind: event.kind };
}

/** The driver's TMS events the view asks its team to acknowledge, for the channel's driver. */
export function requiredTmsEvents(driverMembershipId: string, requires: AcknowledgementRequirement): TmsEventRow[] {
  return rowsByIndex("tmsEvent", idx.tmsEventsByMembership, driverMembershipId).filter(
    (event) => isAcknowledgeableTmsEvent(event) && requires(tmsEventSource(event)),
  );
}

/**
 * One thing on a channel that can wait on an acknowledgement, with when it reached the server.
 */
export type Acknowledgeable =
  | { source: "driverMessage"; arrivedAt: string; item: Item }
  | { source: "tmsEvent"; arrivedAt: string; event: TmsEventRow }
  /** A task about the channel's driver pinned to another board, which arrived when it was made. */
  | { source: "otherBoardTask"; arrivedAt: string; task: OpenTask }
  /** A task on the shelf; {@link acknowledgeablesOn} never yields one, since a channel walk is about items. */
  | { source: "task"; arrivedAt: string; task: OpenTask };

/** What a channel walk yields: an item on the driver's channel, never a task on this board's shelf. */
export type ChannelAcknowledgeable = Exclude<Acknowledgeable, { source: "task" }>;

/**
 * Everything on a channel the view asks its team to acknowledge, of every age the store holds.
 *
 * The driver's live messages when `requires` takes them, then the driver's TMS events of the kinds it takes, then the
 * driver's tasks pinned to other boards when it takes those: the one walk the counts, the watermark and the
 * message-grain rows all read, so they never disagree on what waits.
 *
 * @param feed - The channel, with its driver's membership; a channel without one has messages only.
 * @param requires - What the view asks its team to acknowledge.
 * @param elsewhere - The open tasks pinned to other boards, by driver; none while tasks are off.
 */
export function* acknowledgeablesOn(
  feed: { id: string; aliasMembershipId?: string | null },
  requires: AcknowledgementRequirement,
  elsewhere: TasksElsewhere = NO_TASKS_ELSEWHERE,
): Generator<ChannelAcknowledgeable> {
  if (requires({ source: "driverMessage" })) {
    for (const item of rowsByIndex("item", idx.itemsByFeed, feed.id)) {
      if (isDriverMessage(item)) yield { source: "driverMessage", arrivedAt: item.createdAt, item };
    }
  }
  if (feed.aliasMembershipId) {
    for (const event of requiredTmsEvents(feed.aliasMembershipId, requires)) {
      yield { source: "tmsEvent", arrivedAt: tmsEventArrivedAt(event), event };
    }
    if (requires({ source: "otherBoardTask" })) {
      for (const task of elsewhere.get(feed.aliasMembershipId) ?? []) {
        yield { source: "otherBoardTask", arrivedAt: task.task.createdAt, task };
      }
    }
  }
}

/**
 * An acknowledgeable's own acknowledgements, folded: the message's log, or the event's anchor's.
 */
export function ownAcknowledgements(acknowledgeable: Acknowledgeable): TargetAcknowledgement {
  switch (acknowledgeable.source) {
    case "driverMessage":
      return feedItemFold(acknowledgeable.item.id);
    case "tmsEvent":
      return tmsEventFold(acknowledgeable.event);
    case "otherBoardTask":
    case "task":
      return taskFold(acknowledgeable.task.task.id);
  }
}

/**
 * The subject and target a task names an item by, or null for an event no task can name. A task is never a task's
 * subject, so a task pinned to another board has none.
 */
function taskTargetOf(item: ChannelAcknowledgeable): ItemTarget | null {
  if (item.source === "driverMessage") return { subject: "feed_item", targetId: item.item.id };
  if (item.source === "otherBoardTask") return null;
  return eventTargetOf(item.event);
}

/**
 * Whether an open task keeps an item handled, whatever its own acknowledgements say.
 *
 * A done task keeps nothing handled: the fold holds open tasks alone, so the item falls back to its own log.
 */
export function handledByTask(item: Acknowledgeable, tasks: TaskFold): boolean {
  if (item.source === "task") return false;
  const target = taskTargetOf(item);
  return target !== null && isHandledByTask(tasks.bySubject, target.subject, target.targetId);
}

/** The open task an item carries, the oldest when several name it; null when none does. */
export function openTaskOn(item: Acknowledgeable, tasks: TaskFold): OpenTask | null {
  if (item.source === "task") return null;
  const target = taskTargetOf(item);
  if (target === null) return null;
  return tasks.bySubject.get(taskSubjectKey(target.subject, target.targetId))?.[0] ?? null;
}

/** The channel as {@link acknowledgeablesOn} reads it, from the store. */
function channelOf(feedId: string): { id: string; aliasMembershipId: string | null } {
  return { id: feedId, aliasMembershipId: store.get("feed", feedId)?.aliasMembershipId ?? null };
}

/**
 * How many things on the channel wait on an acknowledgement: the driver's live messages, TMS events and tasks pinned to
 * other boards, each only when the view asks for its kind.
 *
 * Over the items the store holds: warmup loads every channel's last two days, so a message older than that counts only
 * once the channel's history is opened. An item an open task names is handled while the task stays open.
 *
 * @param feedId - The channel.
 * @param now - The clock the lookback is measured from, as epoch milliseconds.
 * @param requires - What the view asks its team to acknowledge.
 * @param tasks - The workspace's open tasks; none while tasks are off.
 * @param elsewhere - The open tasks pinned to other boards, by driver, which wait here when `requires` takes them.
 */
export function feedUnacknowledgedCount(
  feedId: string,
  now: number,
  requires: AcknowledgementRequirement = DEFAULT_REQUIREMENT,
  tasks: TaskFold = NO_TASKS,
  elsewhere: TasksElsewhere = NO_TASKS_ELSEWHERE,
): number {
  const oldest = now - ACKNOWLEDGEMENT_LOOKBACK_MS;
  // Folded once, on the first item inside the lookback: a channel of old traffic never reads its watermark, and one
  // with no watermark does not fold again per item.
  let watermark: FeedWatermark | null = null;
  let watermarkRead = false;
  let count = 0;
  for (const waiting of acknowledgeablesOn(channelOf(feedId), requires, elsewhere)) {
    // Past the lookback an item counts as handled by age, so its acknowledgements need no fold (`isAcknowledged`).
    if (Date.parse(waiting.arrivedAt) < oldest) continue;
    // An open task on the item hands it to the task's board, so it waits on no one here.
    if (handledByTask(waiting, tasks)) continue;
    if (!watermarkRead) {
      watermark = feedWatermark(feedId);
      watermarkRead = true;
    }
    if (!isAcknowledged({ arrivedAt: waiting.arrivedAt, own: ownAcknowledgements(waiting), watermark, now })) {
      count += 1;
    }
  }
  return count;
}

/** The driver alias feeds a driver's TMS event shows on. */
function feedsOfEventDriver(event: Pick<TmsEventRow, "attributedMembershipId">): ReadonlySet<string> {
  return idx.feedsByAliasMembership.keys(event.attributedMembershipId);
}

/** The alias feeds of every driver a task's subjects lead to; none for a task the store does not hold. */
function feedsOfTaskDrivers(taskId: string): string[] {
  const task = store.get("task", taskId);
  if (!task) return [];
  return task.subjects.flatMap(({ workspaceMembershipId }) => [
    ...idx.feedsByAliasMembership.keys(workspaceMembershipId),
  ]);
}

/**
 * Which channels' counts a written row can change, for `useKeyedCompute` over {@link acknowledgementCollections}. An
 * acknowledgement of a TMS event the store does not hold changes no count until the event lands, which is a write of
 * its own.
 */
export const acknowledgementFeedsAffected: AffectedKeys = {
  feed: (feed) => [feed.id],
  item: (item) => [item.feedId],
  feedAcknowledgement: (row) => [row.feedId],
  feedItemAcknowledgement: (row) => [row.feedId],
  tmsEvent: feedsOfEventDriver,
  anchorAcknowledgement: (row) => {
    const event = store.get("tmsEvent", row.anchorId);
    return event ? feedsOfEventDriver(event) : [];
  },
  task: (row) => feedsOfTaskDrivers(row.id),
  taskPinBoard: (row) => feedsOfTaskDrivers(row.taskId),
  taskAcknowledgement: (row) => feedsOfTaskDrivers(row.taskId),
};

/**
 * The newest arrival on the channel the client holds, of the kinds the view asks for: what a watermark written now
 * covers through. Null when the channel holds nothing to cover.
 */
export function feedWatermarkThrough(
  feedId: string,
  requires: AcknowledgementRequirement = DEFAULT_REQUIREMENT,
  elsewhere: TasksElsewhere = NO_TASKS_ELSEWHERE,
): string | null {
  let newest: string | null = null;
  for (const { arrivedAt } of acknowledgeablesOn(channelOf(feedId), requires, elsewhere)) {
    if (newest === null || Date.parse(arrivedAt) > Date.parse(newest)) newest = arrivedAt;
  }
  return newest;
}
